import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useBetting } from '../../../../../../../../context/BettingContext';

const Container = styled.div`
  width: 100%;
  font-family: 'Montserrat Alternates', sans-serif;
  background: #000000;
`;

const Header = styled.div`
  background: rgb(50, 55, 56);
  color: #FF5C5C;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const TabContainer = styled.div`
  display: flex;
  background: rgb(50, 55, 56);
  padding: 0 16px;
`;

const Tab = styled.div`
  padding: 8px 16px;
  color: #ffffff;
  cursor: pointer;
  background: ${props => props.active ? 'rgba(240, 156, 6, 0.2)' : 'transparent'};
  border-radius: 4px 4px 0 0;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const TableContainer = styled.div`
  background: #000000;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 6fr 2fr 2fr 2fr;
  border-bottom: 1px solid rgb(50, 55, 56);
  position: relative;
  background: ${props => props.selected ? 'rgb(50, 55, 56)' : '#000000'};
`;

const MarketName = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #ffffff;
  border-left: 1px solid rgb(50, 55, 56);
  border-right: 1px solid rgb(50, 55, 56);
  display: flex;
  align-items: center; // Vertical center alignment
  min-height: 60px; // Ensure consistent height
  font-family: 'Montserrat Alternates', sans-serif;
`;

const OddsCell = styled.div`
  padding: 4px 5px;
  text-align: center;
  cursor: pointer;
  background: ${props => props.type === 'no' ? '#FAA9BA' : '#bbdefb'};
  display: flex;
  flex-direction: column;
  justify-content: center; // Vertical center alignment
  align-items: center;
  position: relative;
  opacity: ${props => props.suspended || props.ballRunning ? 0.9 : 1};
  border: ${props => {
    if (props.animateNo) return '1px solid rgba(233, 30, 99, 0.4)';
    if (props.animateYes) return '1px solid rgba(33, 150, 243, 0.4)';
    return '1px solid rgb(50, 55, 56)';
  }};
  border-right: 1px solid rgb(50, 55, 56);
  min-height: 40px; // Ensure consistent height
  font-family: 'Montserrat Alternates', sans-serif;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${props => {
    if (props.animateNo) return css`${noOddsChange} 2.5s cubic-bezier(0.4, 0, 0.2, 1)`;
    if (props.animateYes) return css`${yesOddsChange} 2.5s cubic-bezier(0.4, 0, 0.2, 1)`;
    return 'none';
  }};
  
  ${props => props.selected && `
    background: ${props.type === 'no' ? '#FAA9BA' : '#90caf9'};
  `}

  &:hover {
    transform: scale(1.02);
  }
`;

const MainOdds = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: black;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const SubOdds = styled.div`
  font-size: 12px;
  color: #666;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const MinMax = styled.div`
  padding: 4px 8px;
  font-size: 11px;
  color: #cccccc;
  display: flex;
  align-items: center; // Vertical center alignment
  border-right: 1px solid rgb(50, 55, 56);
  min-height: 60px; // Ensure consistent height
  font-family: 'Montserrat Alternates', sans-serif;
`;

const BettingPanel = styled.div`
  background: ${props => props.type === 'no' ? '#FFEBEE' : '#E3F2FD'};
  padding: 12px;
  border-bottom: 1px solid rgb(50, 55, 56);
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Button = styled.div`
  padding: 6px 12px;
  border: 1px solid rgb(50, 55, 56);
  border-radius: 4px;
  background: ${props => props.primary ? 'rgb(50, 55, 56)' : '#000000'};
  color: ${props => props.primary ? '#FF5C5C' : '#ffffff'};
  cursor: pointer;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const StakeInput = styled.input`
  padding: 6px 12px;
  border: 1px solid rgb(50, 55, 56);
  border-radius: 4px;
  width: 120px;
  background: #000000;
  color: #ffffff;
  font-family: 'Montserrat Alternates', sans-serif;
  
  &::placeholder {
    color: #cccccc;
  }
`;

const QuickStakeContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 12px;
  font-family: 'Montserrat Alternates', sans-serif;
`;

// Keyframes for odds change animations
const noOddsChange = keyframes`
  0% { 
    background: linear-gradient(90deg, #FFE6EA, #FFD4DA);
    box-shadow: 0 0 0 0 rgba(255, 182, 193, 0.7);
    border: 1px solid rgba(233, 30, 99, 0.4);
    transform: scale(1);
  }
  25% { 
    background: linear-gradient(90deg, #FFD4DA, #FFB6C1);
    box-shadow: 0 0 8px 2px rgba(255, 182, 193, 0.6);
    border: 1px solid rgba(233, 30, 99, 0.8);
    transform: scale(1.02);
  }
  50% { 
    background: linear-gradient(90deg, #FFB6C1, #FF9BB3);
    box-shadow: 0 0 15px 4px rgba(255, 182, 193, 0.8);
    border: 1px solid rgba(233, 30, 99, 1);
    transform: scale(1.03);
  }
  75% { 
    background: linear-gradient(90deg, #FFD4DA, #FFB6C1);
    box-shadow: 0 0 8px 2px rgba(255, 182, 193, 0.6);
    border: 1px solid rgba(233, 30, 99, 0.8);
    transform: scale(1.02);
  }
  100% { 
    background: linear-gradient(90deg, #FFE6EA, #FFD4DA);
    box-shadow: 0 0 0 0 rgba(255, 182, 193, 0);
    border: 1px solid rgba(233, 30, 99, 0.4);
    transform: scale(1);
  }
`;

const yesOddsChange = keyframes`
  0% { 
    background: linear-gradient(90deg, #E3F2FD, #BBDEFB);
    box-shadow: 0 0 0 0 rgba(100, 181, 246, 0.7);
    border: 1px solid rgba(33, 150, 243, 0.4);
    transform: scale(1);
  }
  25% { 
    background: linear-gradient(90deg, #BBDEFB, #90CAF9);
    box-shadow: 0 0 8px 2px rgba(100, 181, 246, 0.6);
    border: 1px solid rgba(33, 150, 243, 0.8);
    transform: scale(1.02);
  }
  50% { 
    background: linear-gradient(90deg, #64B5F6, #42A5F5);
    box-shadow: 0 0 15px 4px rgba(100, 181, 246, 0.8);
    border: 1px solid rgba(33, 150, 243, 1);
    transform: scale(1.03);
  }
  75% { 
    background: linear-gradient(90deg, #BBDEFB, #90CAF9);
    box-shadow: 0 0 8px 2px rgba(100, 181, 246, 0.6);
    border: 1px solid rgba(33, 150, 243, 0.8);
    transform: scale(1.02);
  }
  100% { 
    background: linear-gradient(90deg, #E3F2FD, #BBDEFB);
    box-shadow: 0 0 0 0 rgba(100, 181, 246, 0);
    border: 1px solid rgba(33, 150, 243, 0.4);
    transform: scale(1);
  }
`;
const FancyBet = ({ matchData, fancyData }) => {
   const { selectedBets, removeBet, placeBets, updateBetStake, updateBetOdds,addBet } = useBetting();
  console.log("Fancy Data:", fancyData);
  console.log("matchDatagvghv",matchData);
  const [selectedMarket, setSelectedMarket] = useState('index');
  const [selectedType, setSelectedType] = useState(null);
  const [stake, setStake] = useState('');
  const [acceptAnyOdds, setAcceptAnyOdds] = useState(false);
  
  // State for tracking odds changes and animations
  const [previousOdds, setPreviousOdds] = useState({});
  const [animatingOdds, setAnimatingOdds] = useState({});
  const prevFancyDataRef = useRef([]);

  const quickStakes = [100, 200, 500, 1000, 2000, 5000];

  // Effect to detect odds changes and trigger animations
  useEffect(() => {
    if (!fancyData || fancyData.length === 0) return;

    const currentOdds = {};
    fancyData.forEach((market, marketIndex) => {
      const noKey = `${marketIndex}-no`;
      const yesKey = `${marketIndex}-yes`;
      currentOdds[noKey] = {
        odds: market.oddsNo,
        runs: market.runsNo
      };
      currentOdds[yesKey] = {
        odds: market.oddsYes,
        runs: market.runsYes
      };
    });

    // Check for changes and trigger animations
    const newAnimatingOdds = {};
    fancyData.forEach((market, marketIndex) => {
      const noKey = `${marketIndex}-no`;
      const yesKey = `${marketIndex}-yes`;
      const prevNo = previousOdds[noKey];
      const prevYes = previousOdds[yesKey];
      
      if (prevNo && (prevNo.odds !== market.oddsNo || prevNo.runs !== market.runsNo)) {
        newAnimatingOdds[noKey] = true;
      }
      if (prevYes && (prevYes.odds !== market.oddsYes || prevYes.runs !== market.runsYes)) {
        newAnimatingOdds[yesKey] = true;
      }
    });

    if (Object.keys(newAnimatingOdds).length > 0) {
      setAnimatingOdds(newAnimatingOdds);
      
      // Clear animation after 2.5 seconds
      setTimeout(() => {
        setAnimatingOdds({});
      }, 2500);
    }

    // Update previous odds
    setPreviousOdds(currentOdds);
    prevFancyDataRef.current = fancyData;
  }, [fancyData]);

  const handleSelect = (marketIndex, type) => {

  //     if (market.statusName !== "ACTIVE") {
  //   console.log("Market not active:", market.statusName);
  //   return;
  // }

    
    setSelectedMarket(marketIndex);
    setSelectedType(type);
    handlePlaceBet(marketIndex,type);
  };

  const handlePlaceBet = async(marketIndex,type) => {
  // if (selectedMarket === null || !selectedType ) {
  //   alert("Please select a market, type, and enter stake");
  //   return;
  // }

  const market = fancyData[marketIndex];

  const betDetails = {
    matchId: matchData.id,
    eventid: matchData.eventId,
    matchTitle: `${matchData.team1} v ${matchData.team2}`,
    team1: matchData.team1,
    team2: matchData.team2,
    marketId: market.marketId,
    marketName: market.marketName,
    selectionName: type === "yes" ? "Yes" : "No",
    type: type,
    odds:
      type === "yes"
        ? market.oddsYes
        : market.oddsNo,
    runs:
      type === "yes"
        ? market.runsYes
        : market.runsNo,
    stake:0,
    min: market.minSetting,
    max: market.maxSetting,
    acceptAnyOdds
  };

  console.log("Placing bet:", betDetails);
 addBet(betDetails);
  // 🔹 send to your bet context / API
//  const result = await placeBets(betDetails);
 
     // Log result
    //  console.log('PlaceBets result:', result);
 
    //  if (result?.confirmed?.length > 0) {
    //    // alert('Bets placed successfully!');
    //    sendNotfication('Bets placed successfully!', 'success', 'bottom-left')
    //    // Clear only confirmed from slip handled by context; local UI already synced
    //  } else {
    //    const firstError = result?.errors?.[0]?.message || 'Bet placement failed';
    //    // alert(firstError);
    //     sendNotfication(firstError, 'error', 'bottom-left')
    //    return;
    //  }

  // Reset after placing bet
  setStake('');
  setSelectedMarket(null);
  setSelectedType(null);
};


  return (
    <Container>
      <Header>
        <span>⭐</span>
        <span>Fancy Bet</span>
      </Header>
      
      {/* <TabContainer>
        <Tab active>All</Tab>
        <Tab>Fancy</Tab>
        <Tab>Ball by Ball</Tab>
        <Tab>Khadda</Tab>
        <Tab>Lottery</Tab>
        <Tab>Odd/Even</Tab>
      </TabContainer> */}

      <TableContainer>
        {fancyData.map((market, index) => (
          <React.Fragment key={market.marketId}>
            <TableRow selected={selectedMarket === index}>
              <MarketName>{market.marketName}</MarketName>
              <OddsCell 
                type="no"
                selected={selectedMarket === index && selectedType === 'no'}
                suspended={market.statusName === 'SUSPEND'}
                ballRunning={market.statusName === 'BALL_RUN'}
                onClick={() => handleSelect(index, 'no')}
                animateNo={animatingOdds[`${index}-no`]}
              >
                <MainOdds>{market.runsNo}</MainOdds>
                <SubOdds>{market.oddsNo}</SubOdds>
                {market.statusName !== 'ACTIVE' && (
                  <StatusOverlay>
                    {market.statusName === 'SUSPEND' ? 'Suspend' : 'Ball Running'}
                  </StatusOverlay>
                )}
              </OddsCell>
              <OddsCell 
                type="yes"
                selected={selectedMarket === index && selectedType === 'yes'}
                suspended={market.statusName === 'SUSPEND'}
                ballRunning={market.statusName === 'BALL_RUN'}
                onClick={() => handleSelect(index, 'yes')}
                animateYes={animatingOdds[`${index}-yes`]}
              >
                <MainOdds>{market.runsYes}</MainOdds>
                <SubOdds>{market.oddsYes}</SubOdds>
                {market.statusName !== 'ACTIVE' && (
                  <StatusOverlay>
                    {market.statusName === 'SUSPEND' ? 'Suspend' : 'Ball Running'}
                  </StatusOverlay>
                )}
              </OddsCell>
              <MinMax>{`${market.minSetting} / ${market.maxSetting}`}</MinMax>
            </TableRow>
            
            {/* {selectedMarket === index && (
              <BettingPanel type={selectedType}>
                <CheckboxContainer>
                  <input 
                    type="checkbox" 
                    checked={acceptAnyOdds}
                    onChange={(e) => setAcceptAnyOdds(e.target.checked)}
                  />
                  <span style={{color: 'black'}}>Accept Any Odds</span>
                </CheckboxContainer>
                
                <ActionContainer>
                  <Button onClick={() => setSelectedMarket(null)}>Cancel</Button>
                  <StakeInput 
                    type="text"
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    placeholder="Enter stake"
                  />
                  <Button primary >Place Bets</Button>
                </ActionContainer>
            
                <QuickStakeContainer>
                  {quickStakes.map(value => (
                    <Button key={value} onClick={() => setStake(value.toString())}>
                      {value}
                    </Button>
                  ))}
                </QuickStakeContainer>
              </BettingPanel>
            )} */}
          </React.Fragment>
        ))}
      </TableContainer>
    </Container>
  );
};

export default FancyBet;